import {
  Typography,
  Container,
  Grid,
  CircularProgress,
  Box,
  Tooltip,
} from "@mui/material";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useMemo } from "react";
import CreatePost from "../components/CreatePost";
import PostsList from "../components/PostsList";
import AddFriends from "../components/AddFriends";
import { fetchPosts } from "../features/postsSlice";
import { fetchUsers } from "../features/usersSlice";
import Banner from "../components/Banner";
import OnlineFriends from "../components/OnlineFriends";

const Home = () => {
  const dispatch = useDispatch();

  const { posts, isLoading: postsLoading, error: postsError } = useSelector(
    (state) => state.posts
  );
  const { users, isLoading: usersLoading, error: usersError } = useSelector(
    (state) => state.users
  );
  const currentUser = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [allOrFollowings, setAllOrFollowings] = useState("all");

  useEffect(() => {
    dispatch(fetchPosts());
    dispatch(fetchUsers());
  }, [dispatch]);

  const followingsPosts = useMemo(() => {
    return currentUser?.following
      ? posts.filter((p) => currentUser.following.includes(p.author._id))
      : [];
  }, [posts, currentUser]);

  if (postsLoading || usersLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 65px)",
          gap: 2,
          bgcolor: "#F0F2F5",
        }}
      >
        <CircularProgress sx={{ color: "#D4AF37" }} />
        <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>
          Loading your feed…
        </Typography>
      </Box>
    );
  }

  if (postsError)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">Error loading posts: {postsError}</Typography>
      </Box>
    );
  if (usersError)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">Error loading users: {usersError}</Typography>
      </Box>
    );

  return (
    <Box sx={{ bgcolor: "#F0F2F5", minHeight: "calc(100vh - 65px)", pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Banner />

        <Grid container spacing={2}>
          {/* Left sidebar */}
          <Grid
            item
            lg={2.5}
            sx={{ display: { xs: "none", sm: "none", lg: "block" } }}
          >
            <Box sx={sidebarSx}>
              <OnlineFriends onlineUsers={users} />
            </Box>
          </Grid>

          {/* Main feed */}
          <Grid item xs={12} md={12} lg={7}>
            {isAuthenticated && <CreatePost />}

            {/* Feed toggle */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Tooltip title="All posts">
                <Box
                  onClick={() => setAllOrFollowings("all")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    px: 2.5,
                    py: 1,
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 13.5,
                    transition: "all 0.18s",
                    bgcolor: allOrFollowings === "all" ? "#1A1A2E" : "#fff",
                    color: allOrFollowings === "all" ? "#fff" : "#6B7280",
                    boxShadow: allOrFollowings === "all"
                      ? "0 2px 8px rgba(26,26,46,0.25)"
                      : "0 1px 3px rgba(0,0,0,0.08)",
                    "&:hover": {
                      bgcolor: allOrFollowings === "all" ? "#16213E" : "#F3F4F6",
                    },
                  }}
                >
                  <PublicRoundedIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                    All Posts
                  </Typography>
                </Box>
              </Tooltip>

              {isAuthenticated && (
                <Tooltip title="Following feed">
                  <Box
                    onClick={() => setAllOrFollowings("followings")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      px: 2.5,
                      py: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      transition: "all 0.18s",
                      bgcolor: allOrFollowings === "followings" ? "#1A1A2E" : "#fff",
                      color: allOrFollowings === "followings" ? "#fff" : "#6B7280",
                      boxShadow: allOrFollowings === "followings"
                        ? "0 2px 8px rgba(26,26,46,0.25)"
                        : "0 1px 3px rgba(0,0,0,0.08)",
                      "&:hover": {
                        bgcolor: allOrFollowings === "followings" ? "#16213E" : "#F3F4F6",
                      },
                    }}
                  >
                    <GroupRoundedIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                      Following
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>

            <PostsList
              posts={posts}
              followingsPosts={followingsPosts}
              allOrFollowings={allOrFollowings}
            />
          </Grid>

          {/* Right sidebar */}
          <Grid
            item
            lg={2.5}
            sx={{ display: { xs: "none", sm: "none", lg: "block" } }}
          >
            <Box sx={sidebarSx}>
              <AddFriends />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const sidebarSx = {
  position: "sticky",
  top: 80,
  bgcolor: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  p: 2,
  maxHeight: "calc(100vh - 100px)",
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": { bgcolor: "#E5E7EB", borderRadius: 2 },
};

export default Home;
